import { FALLBACK_HOST, imageSrc } from '@/lib/images'
import { formatSlot, isOnAir, sortSchedule } from '@/lib/time'
import type { TeamMember } from '@/types/radio'
import {ChevronLeft, ChevronRight, Clock, Headphones, Sun} from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'

interface TeamCarouselProps {
  team: TeamMember[]
}

function IconBadge({ icon }: { icon?: string }) {
  if (icon === 'headphones') return <Headphones className="h-3 w-3 sm:h-4 sm:w-4" />
  if (icon === 'hat') return <span className="text-[10px] sm:text-sm">🤠</span>
  return <Sun className="h-3 w-3 sm:h-4 sm:w-4" />
}

function TeamCarousel({ team }: TeamCarouselProps) {
  const scroller = useRef<HTMLDivElement>(null)
  const paused = useRef(false)
  const members = useMemo(
    () => sortSchedule((team || []).filter((item) => item?.active)),
    [team],
  )

  const scrollByCard = (dir: number) => {
    const el = scroller.current
    if (!el) return
    const card = el.querySelector('article')
    const gap = window.innerWidth < 640 ? 10 : 16
    const step = card ? card.getBoundingClientRect().width + gap : 148
    const max = el.scrollWidth - el.clientWidth
    if (dir > 0 && el.scrollLeft >= max - 8) {
      el.scrollTo({ left: 0, behavior: 'smooth' })
      return
    }
    if (dir < 0 && el.scrollLeft <= 8) {
      el.scrollTo({ left: max, behavior: 'smooth' })
      return
    }
    el.scrollBy({ left: dir * step, behavior: 'smooth' })
  }

  useEffect(() => {
    if (members.length < 2) return undefined
    const timer = window.setInterval(() => {
      if (paused.current) return
      scrollByCard(1)
    }, 3800)
    return () => window.clearInterval(timer)
  }, [members.length])

  return (
    <section id="equipe" className="mx-auto mt-12 max-w-7xl px-3 sm:mt-16 sm:px-4 lg:mt-20 lg:px-6">
      <div className="mb-4 flex items-end justify-between gap-3 sm:mb-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-[#18c8f5] text-[#071a63] sm:h-10 sm:w-10">
              <Headphones size={18} />
            </span>
            <h2 className="font-display text-xl font-black tracking-wide sm:text-3xl">NOSSA EQUIPE</h2>
          </div>
          <p className="mt-1 text-[10px] font-semibold tracking-[0.16em] text-white/70 sm:text-xs sm:tracking-[0.18em]">
            CONHEÇA OS APRESENTADORES E SEUS PROGRAMAS
          </p>
        </div>
        <a href="/#programacao" className="hidden rounded-full border border-white/20 px-4 py-2 text-xs font-bold sm:inline-flex">
          VER TODOS {'>'}
        </a>
      </div>

      <div
        className="relative"
        onMouseEnter={() => { paused.current = true }}
        onMouseLeave={() => { paused.current = false }}
        onTouchStart={() => { paused.current = true }}
        onTouchEnd={() => { paused.current = false }}
      >
        <button
          type="button"
          onClick={() => scrollByCard(-1)}
          className="absolute left-0 top-[42%] z-10 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white text-[#0b2ea8] shadow sm:h-10 sm:w-10 md:-left-3"
          aria-label="Anterior"
        >
          <ChevronLeft size={18} />
        </button>
        <div
          ref={scroller}
          className="no-scrollbar flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth px-9 pb-2 sm:gap-4 sm:px-12"
        >
          {members.map((member) => (
            <article
              key={member._id || member.name}
              className="w-[126px] shrink-0 snap-start overflow-hidden rounded-2xl bg-white text-[#12307a] shadow-lg sm:w-[176px] sm:rounded-[26px] md:w-[210px] md:rounded-[28px]"
            >
              <div className="relative h-[102px] bg-gradient-to-b from-[#ffb400] to-[#ff7a00] sm:h-[156px] md:h-[198px]">
                {isOnAir(member.startTime, member.endTime, member.days) ? (
                  <span className="live-badge-pulse absolute left-1/2 top-1.5 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[8px] font-black tracking-wide text-white shadow-md sm:top-2 sm:px-2.5 sm:py-1 sm:text-[10px]">
                    <span className="h-1.5 w-1.5 rounded-full bg-white sm:h-2 sm:w-2" />
                    AO VIVO
                  </span>
                ) : null}
                <img src={imageSrc(member.photoUrl, FALLBACK_HOST)} alt={member.name} className="h-full w-full object-cover object-top" />
              </div>
              <div className="relative px-2 pb-2.5 pt-3.5 sm:px-4 sm:pb-4 sm:pt-5">
                <span className="absolute -top-3 left-2 grid h-5 w-5 place-items-center rounded-full bg-[#ffd21f] text-[#0b2ea8] sm:-top-4 sm:left-4 sm:h-8 sm:w-8">
                  <IconBadge icon={member.icon} />
                </span>
                <h3 className="font-display text-[12px] font-black leading-tight sm:text-base md:text-lg">{member.name}</h3>
                <p className="line-clamp-2 text-[10px] italic text-[#2a63e8] sm:text-sm">{member.programName}</p>
                <p className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-[#12307a] sm:mt-3 sm:gap-1.5 sm:text-sm">
                  <Clock size={11} /> {formatSlot(member.startTime, member.endTime)}
                </p>
                <div className="mt-1 flex flex-wrap gap-0.5 sm:mt-2 sm:gap-1">
                  {(member.days || []).map((day) => (
                    <span key={day} className="rounded-md bg-[#edf3ff] px-1 py-0.5 text-[7px] font-extrabold text-[#2a63e8] sm:px-1.5 sm:text-[10px]">
                      {day}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
        <button
          type="button"
          onClick={() => scrollByCard(1)}
          className="absolute right-0 top-[42%] z-10 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white text-[#0b2ea8] shadow sm:h-10 sm:w-10 md:-right-3"
          aria-label="Seguinte"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </section>
  )
}

export default TeamCarousel
